import React, { useState } from 'react';
import { Button, Card as AntCard, Empty, Form, Input, Modal, Popconfirm, Select, Space, Typography, Upload } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFlask, faPlus } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import {
  useKnowledgeQuery,
  useRemoveKnowledgeMutation,
  useSaveKnowledgeMutation,
  useTestKnowledgeMutation,
  useUploadKnowledgeMutation
} from '../../queries/settings';
import { Card, CardHead, TonePill } from '../../components';
import { HeadText, StackTable } from './styled';

const TYPES = [
  { value: 'faq', label: 'FAQ' },
  { value: 'text', label: 'Note / policy' },
  { value: 'url', label: 'Web page' }
];
const TYPE_LABEL = { faq: 'FAQ', text: 'Note', url: 'Web page', pdf: 'PDF' };

function EntryModal({ entry, onClose }) {
  const [form] = Form.useForm();
  const type = Form.useWatch('sourceType', form);
  const { mutate, isPending } = useSaveKnowledgeMutation();
  const submit = (values) => mutate({ id: entry._id, ...values }, { onSuccess: onClose });

  return (
    <Modal
      title={entry._id ? 'Edit entry' : 'Add entry'}
      open
      onCancel={onClose}
      onOk={form.submit}
      okText="Save"
      confirmLoading={isPending}
      width={620}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        requiredMark={false}
        initialValues={{ sourceType: 'faq', ...entry }}
      >
        <Form.Item name="title" label="Title / question" rules={[{ required: true, whitespace: true }]}>
          <Input placeholder="e.g. Do you allow pets?" />
        </Form.Item>
        <Form.Item name="sourceType" label="Type">
          <Select options={entry.sourceType === 'pdf' ? [...TYPES, { value: 'pdf', label: 'PDF' }] : TYPES} />
        </Form.Item>
        {type === 'url' && (
          <Form.Item
            name="sourceUrl"
            label="Page address"
            extra="Leave the content blank and we read the page for you."
            rules={[{ type: 'url', message: 'Enter a full address, e.g. https://…' }]}
          >
            <Input placeholder="https://" />
          </Form.Item>
        )}
        <Form.Item
          name="content"
          label="Answer / content"
          rules={[{ required: type !== 'url', whitespace: true, message: 'Write the answer' }]}
        >
          <Input.TextArea rows={7} placeholder="What the assistant should say…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function TestModal({ onClose }) {
  const [query, setQuery] = useState('');
  const { mutate, data, isPending } = useTestKnowledgeMutation();
  const hits = data?.hits || [];
  const run = () => query.trim() && mutate(query.trim());

  return (
    <Modal title="Test what the assistant would find" open onCancel={onClose} footer={null} width={620}>
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input
          aria-label="Test question"
          placeholder="Ask a question a caller might ask…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={run}
        />
        <Button type="primary" loading={isPending} onClick={run}>
          Test
        </Button>
      </Space.Compact>
      {hits.length === 0 ? (
        <Empty description={data ? 'Nothing matched' : 'No results yet'} />
      ) : (
        hits.map((h, i) => (
          <AntCard key={i} size="small" style={{ marginBottom: 8 }}>
            <Typography.Text strong>{h.title}</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {h.text}
            </Typography.Paragraph>
          </AntCard>
        ))
      )}
    </Modal>
  );
}

export default function KnowledgeCard() {
  const { data: docs = [], isPending } = useKnowledgeQuery();
  const remove = useRemoveKnowledgeMutation();
  const upload = useUploadKnowledgeMutation();
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(false);

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (v, r) => (
        <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => setEditing(r)}>
          {v}
        </Button>
      )
    },
    { title: 'Type', dataIndex: 'sourceType', width: 110, render: (v) => <TonePill $tone="info">{TYPE_LABEL[v] || v}</TonePill> },
    { title: 'Updated', dataIndex: 'updatedAt', width: 130, render: (v) => (v ? dayjs(v).format('MMM D, YYYY') : '—') },
    {
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Popconfirm title="Delete this entry?" okButtonProps={{ danger: true }} onConfirm={() => remove.mutate(r._id)}>
          <Button type="text" danger size="small">
            Delete
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Card aria-labelledby="knowledge-title">
      <CardHead style={{ justifyContent: 'space-between' }}>
        <HeadText>
          <h2 id="knowledge-title">Knowledge &amp; FAQ</h2>
          <span>answers your assistant uses when callers ask questions</span>
        </HeadText>
        <Space wrap>
          <Button icon={<FontAwesomeIcon icon={faFlask} />} onClick={() => setTesting(true)}>
            Test answers
          </Button>
          <Upload
            accept="application/pdf"
            showUploadList={false}
            beforeUpload={(file) => {
              upload.mutate(file);
              return false;
            }}
          >
            <Button icon={<FontAwesomeIcon icon={faFilePdf} />} loading={upload.isPending}>
              Upload PDF
            </Button>
          </Upload>
          <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setEditing({})}>
            Add entry
          </Button>
        </Space>
      </CardHead>
      <StackTable
        rowKey="_id"
        size="middle"
        loading={isPending}
        columns={columns}
        dataSource={docs}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: 520 }}
        locale={{ emptyText: <Empty description="No entries yet. Add your first FAQ." /> }}
      />
      {editing && <EntryModal entry={editing} onClose={() => setEditing(null)} />}
      {testing && <TestModal onClose={() => setTesting(false)} />}
    </Card>
  );
}
